import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { listBlogPosts } from "@/lib/api";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        listBlogPosts()
            .then((data) => setPosts(data))
            .catch((e) => setError(e.message || "Erreur"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <main data-testid="blog-page" style={{ paddingTop: 140 }}>
            <section style={{ padding: "40px 40px 60px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                            Journal · Repères cliniques
                        </div>
                        <div className="divider-terra mt-3" />
                        <h1
                            className="font-display mt-8"
                            style={{
                                fontSize: "clamp(3rem, 7vw, 6.5rem)",
                                lineHeight: 0.95,
                                color: "var(--forest-dark)",
                                fontWeight: 400,
                                letterSpacing: "-0.02em",
                                maxWidth: 1100,
                            }}
                        >
                            Des <span style={{ fontStyle: "italic", color: "var(--terra)" }}>notes cliniques</span>,
                            <br />pas des <span style={{ fontStyle: "italic" }}>promesses.</span>
                        </h1>
                        <p
                            className="font-display mt-8"
                            style={{
                                fontSize: "1.2rem",
                                lineHeight: 1.6,
                                color: "var(--ink-mid)",
                                fontStyle: "italic",
                                maxWidth: 720,
                            }}
                        >
                            Repères pratiques, protocoles de prise en charge et points d'attention
                            pour orienter au mieux les patients — à destination des médecins
                            référents et des confrères physiothérapeutes.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Posts grid */}
            <section style={{ padding: "40px 40px 120px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    {loading && (
                        <div style={{ fontSize: 14, color: "var(--ink-soft)", padding: "40px 0" }}>
                            Chargement des articles…
                        </div>
                    )}
                    {error && (
                        <div style={{ fontSize: 14, color: "var(--destructive)" }}>
                            Impossible de charger les articles : {error}
                        </div>
                    )}
                    {!loading && posts.length === 0 && !error && (
                        <div>Aucun article publié pour l'instant.</div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                        {posts.map((p, i) => (
                            <motion.article
                                key={p.slug}
                                data-testid={`blog-card-${p.slug}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.8, delay: (i % 3) * 0.1 }}
                                className={i === 0 ? "md:col-span-8" : "md:col-span-4"}
                            >
                                <Link
                                    to={`/journal/${p.slug}`}
                                    style={{ textDecoration: "none", color: "inherit", display: "block" }}
                                >
                                    <div
                                        style={{
                                            position: "relative",
                                            aspectRatio: i === 0 ? "16 / 9" : "4 / 3",
                                            overflow: "hidden",
                                            borderRadius: 4,
                                            marginBottom: 20,
                                        }}
                                    >
                                        <img
                                            src={p.cover_image}
                                            alt=""
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                transition: "transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                                            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                        />
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: 16,
                                                left: 16,
                                                background: "var(--cream)",
                                                padding: "6px 12px",
                                                fontSize: 10,
                                                letterSpacing: "0.16em",
                                                textTransform: "uppercase",
                                                color: "var(--forest-dark)",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {p.category}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4" style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: "0.14em" }}>
                                        <span>{new Date(p.published_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" })}</span>
                                        <span className="flex items-center gap-1">
                                            <Clock size={11} /> {p.reading_time} min
                                        </span>
                                    </div>

                                    <h2
                                        className="font-display mt-4"
                                        style={{
                                            fontSize: i === 0 ? "clamp(1.8rem, 3.5vw, 2.6rem)" : "1.5rem",
                                            lineHeight: 1.15,
                                            color: "var(--forest-dark)",
                                            fontWeight: 400,
                                        }}
                                    >
                                        {p.title}
                                    </h2>
                                    <p
                                        style={{
                                            fontSize: 14.5,
                                            lineHeight: 1.6,
                                            color: "var(--ink-mid)",
                                            marginTop: 12,
                                            maxWidth: 640,
                                        }}
                                    >
                                        {p.excerpt}
                                    </p>
                                    <div
                                        className="mt-4 inline-flex items-center gap-2 link-underline"
                                        style={{ fontSize: 13, letterSpacing: "0.03em", color: "var(--terra)" }}
                                    >
                                        Lire la note <ArrowRight size={13} />
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
